import LetrusLogo from 'assets/images/icons/letrus-logo.webp';
import {Navbar} from 'letrus-ui';
import {Button, Image} from 'components/ui';
import {Link} from 'react-router-dom';
import {useEffect, useRef, useState} from 'react';
import Notifications from 'components/Notifications';
import {getUserData} from 'utils/functions/getUserData/getUserData';
import {useLogout} from 'APIs/letrusAPI/logout';
import FocusModeLogoutModal from 'components/FocusModeLogoutModal';
import Menu from '../Menu';
import AppStatusBar from './AppStatusBar';
import styles from './PageWrapper.module.scss';

export interface PageWrapperProps {
  children: React.ReactNode;
}

function PageWrapper({children}: PageWrapperProps) {
  const logout = useLogout();
  const {userRole} = getUserData();
  const externalLogo = userRole?.network_logo;

  const isActiveFocusModeRef = useRef(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [focusModeClosedCount, setFocusModeClosedCount] = useState(0);

  useEffect(() => {
    const handler = (e: Event) => {
      const {isActive, focusModeClosedCount: count} = (
        e as CustomEvent<{isActive: boolean; focusModeClosedCount: number}>
      ).detail;
      isActiveFocusModeRef.current = isActive;
      setFocusModeClosedCount(count ?? 0);
    };
    window.addEventListener('focusModeStatus', handler);
    return () => window.removeEventListener('focusModeStatus', handler);
  }, []);

  function handleLogoutClick() {
    if (isActiveFocusModeRef.current) {
      setShowLogoutModal(true);
      return;
    }
    logout.trigger();
  }

  function handleConfirmLogout() {
    setShowLogoutModal(false);
    window.dispatchEvent(new CustomEvent('focusModeIntentionalLogout'));
    setTimeout(() => logout.trigger(), 0);
  }

  const isInIframe = (() => {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  })();

  return (
    <div className={styles.wrapper}>
      {!isInIframe && (
        <Navbar
          rightComponent={
            <div className={styles.rightComponent}>
              <AppStatusBar />
              <div data-focusid="notificationBell">
                <Notifications />
              </div>

              <div data-focusid="logoutButton">
                <Button
                  leftIcon={{icon: 'sign-out-alt', color: '#ffbf00'}}
                  text="Sair"
                  variant="secondary"
                  onClick={handleLogoutClick}
                  borderColor="#ffbf00"
                  boxShadow="#cc9706"
                  size="small"
                />
              </div>

              <Menu onLogoutClick={handleLogoutClick} />
            </div>
          }
          leftComponent={
            <Link to="/">
              {externalLogo && <img src={externalLogo} alt="Logo da rede" />}
              <Image alt="Logo Letrus" imageSrc={LetrusLogo} />
            </Link>
          }
        />
      )}

      <div className={styles.children}>{children}</div>

      {showLogoutModal && (
        <FocusModeLogoutModal
          focusModeClosedCount={focusModeClosedCount}
          onContinue={() => setShowLogoutModal(false)}
          onConfirmLogout={handleConfirmLogout}
        />
      )}
    </div>
  );
}

export default PageWrapper;
